/**
 * Maker-checker (guide §5.4) — mécanisme GENERIQUE de soumission d'une
 * demande d'approbation, partagé entre features.
 *
 * Cinq actions ne peuvent jamais être exécutées directement : activation
 * d'une règle de frais (FEE_RULE_CHANGE), contre-passation comptable
 * manuelle (LEDGER_MANUAL_REVERSAL), forçage d'un statut de criblage
 * sanctions (KYC_FORCE_OVERRIDE), émission et retrait de monnaie
 * électronique (EMONEY_ISSUANCE / EMONEY_REDEMPTION).
 *
 * Vit dans `shared` parce que les features ne s'importent jamais entre
 * elles : fee-engine/kyc/ledger soumettent leurs demandes via ce hook, qui
 * appelle les endpoints admin ré-exportés par `@/core/api/endpoints`.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  adminApprovalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { endpoints } from "@/core/api/endpoints";
import { useSessionStore } from "@/core/stores/session.store";
import { logger } from "@/core/utils/func/logger";
import type {
  ApprovalActionType,
  ApprovalStatus,
} from "@/features/shared/api/enums";

/** Entrée de soumission d'une demande d'approbation (côté demandeur). */
export interface SubmitApprovalRequestInput {
  actionType: ApprovalActionType;
  /** Service émetteur de l'action protégée (ex. "fee-engine-service"). */
  sourceService: string;
  /** Type de ressource visée (ex. "FeeRule", "Party"). */
  resourceType: string;
  /** Identifiant EXACT de la ressource qui sera modifiée. */
  resourceId: string;
  /**
   * Charge utile structurée de l'action — sérialisée en JSON (chaîne) par
   * le hook, le backend attend une `payload` stringifiée.
   */
  payload?: Record<string, unknown>;
  /** Motif métier obligatoire (visible par l'approbateur). */
  reason: string;
}

/** Réponse utile de `POST /ApprovalRequests/add` (201). */
export interface SubmittedApprovalRequest {
  id: string;
  status: ApprovalStatus;
}

/**
 * Ouvre une demande d'approbation au statut PENDING
 * (`POST /gw/admin/api/v1/ApprovalRequests/add`). `requestedByKeycloakId`
 * est le claim `sub` de l'opérateur connecté. Rôles : flashpay-superadmin,
 * flashpay-admin, flashpay-service (§6.8).
 */
export const useSubmitApprovalRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["shared", "maker-checker", "submit-approval-request"],
    mutationFn: (input: SubmitApprovalRequestInput) => {
      const requestedByKeycloakId = useSessionStore.getState().userInfo?.sub;
      return mutateData({
        url: endpoints.admin.approvalRequests.add,
        reqBody: {
          actionType: input.actionType,
          sourceService: input.sourceService,
          resourceType: input.resourceType,
          resourceId: input.resourceId,
          payload: JSON.stringify(input.payload ?? {}),
          reason: input.reason,
          requestedByKeycloakId,
        },
        method: "POST",
      }) as Promise<SubmittedApprovalRequest>;
    },
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, adminApprovalCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSubmitApprovalRequest failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "maker-checker" }
      );
    },
  });
};
